import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import AISuggestionsBanner from "../components/AISuggestionsBanner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Layers,
  ChevronDown,
  ChevronUp,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Building2,
  AlertCircle,
  Info,
  Loader2,
  Sparkles,
} from "lucide-react";

const CRITICALITY_STYLES = {
  critical: "bg-red-100 text-red-700 border-red-300",
  high: "bg-orange-100 text-orange-700 border-orange-300",
  medium: "bg-amber-100 text-amber-700 border-amber-300",
  low: "bg-slate-100 text-slate-600 border-slate-300",
};

export default function ServiceLineManagementPage() {
  const { api, user } = useAuth();
  const [org, setOrg] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [addInputs, setAddInputs] = useState({});
  const [editing, setEditing] = useState(null); // { deptId, index, value }
  const [deleteTarget, setDeleteTarget] = useState(null); // { deptId, index, name }
  const [savingDept, setSavingDept] = useState(null);
  // Department that unmatched AI suggestions are added to (user-controlled).
  const [fallbackDeptId, setFallbackDeptId] = useState("");
  // Reliable session provenance: only lines added/accepted this session.
  const [sessionSources, setSessionSources] = useState({}); // `${deptId}::${nameLower}` -> 'manual' | 'ai'

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [orgRes, deptsRes] = await Promise.all([
        api.get("/bcm-admin/organization").catch(() => ({ data: {} })),
        api.get("/bcm-admin/departments"),
      ]);
      setOrg(orgRes.data || {});
      const depts = (deptsRes.data || []).map((d) => ({ ...d, service_lines: d.service_lines || [] }));
      setDepartments(depts);
      if (depts.length > 0) setFallbackDeptId((prev) => prev || depts[0].id);
    } catch (e) {
      toast.error("Failed to load departments");
    } finally {
      setLoading(false);
    }
  };

  const metrics = useMemo(() => {
    const totalDepts = departments.length;
    const totalLines = departments.reduce((n, d) => n + (d.service_lines?.length || 0), 0);
    const emptyDepts = departments.filter((d) => !(d.service_lines?.length)).length;
    return { totalDepts, totalLines, emptyDepts };
  }, [departments]);

  const allServiceLineNames = useMemo(
    () => departments.flatMap((d) => d.service_lines || []),
    [departments]
  );

  const sourceOf = (deptId, name) =>
    sessionSources[`${deptId}::${String(name).trim().toLowerCase()}`];

  // Hide meaningless recovery targets like "0h" / "0".
  const isMeaningful = (v) => v != null && String(v).trim() !== "" && !/^0\s*[hmd]?$/i.test(String(v).trim());

  const persist = async (deptId, serviceLines) => {
    setSavingDept(deptId);
    try {
      await api.put(`/bcm-admin/departments/${deptId}`, { service_lines: serviceLines });
      setDepartments((prev) => prev.map((d) => (d.id === deptId ? { ...d, service_lines: serviceLines } : d)));
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save service lines");
      return false;
    } finally {
      setSavingDept(null);
    }
  };

  const markSource = (deptId, name, source) => {
    setSessionSources((prev) => ({ ...prev, [`${deptId}::${String(name).trim().toLowerCase()}`]: source }));
  };

  const handleAdd = async (dept) => {
    const value = (addInputs[dept.id] || "").trim();
    if (!value) return;
    if ((dept.service_lines || []).some((s) => s.toLowerCase() === value.toLowerCase())) {
      toast.error("That service line already exists in this department");
      return;
    }
    const next = [...(dept.service_lines || []), value];
    const ok = await persist(dept.id, next);
    if (ok) {
      markSource(dept.id, value, "manual");
      setAddInputs((p) => ({ ...p, [dept.id]: "" }));
      toast.success("Service line added");
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    const { deptId, index, value } = editing;
    const trimmed = value.trim();
    if (!trimmed) return;
    const dept = departments.find((d) => d.id === deptId);
    if (!dept) return;
    if ((dept.service_lines || []).some((s, i) => i !== index && s.toLowerCase() === trimmed.toLowerCase())) {
      toast.error("That service line already exists in this department");
      return;
    }
    const next = [...dept.service_lines];
    const prevName = next[index];
    next[index] = trimmed;
    const ok = await persist(deptId, next);
    if (ok) {
      const src = sourceOf(deptId, prevName);
      if (src) markSource(deptId, trimmed, src);
      setEditing(null);
      toast.success("Service line updated");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const { deptId, index } = deleteTarget;
    const dept = departments.find((d) => d.id === deptId);
    if (!dept) return;
    const next = dept.service_lines.filter((_, i) => i !== index);
    const ok = await persist(deptId, next);
    if (ok) {
      setDeleteTarget(null);
      toast.success("Service line deleted");
    }
  };

  // AI banner: payload is an array of { name, description, department } objects.
  const handleAcceptSuggestions = async (payload) => {
    const fallback = departments.find((d) => d.id === fallbackDeptId) || departments[0];
    const updates = {}; // deptId -> new service_lines array
    const acceptedNames = {}; // deptId -> [names]
    let created = 0;
    let reroutedCount = 0;

    for (const item of payload || []) {
      const name = item && typeof item === "object" ? item.name : item;
      const deptName = item && typeof item === "object" ? item.department : null;
      if (!name || !String(name).trim()) continue;
      const clean = String(name).trim();

      let dept = null;
      if (deptName) {
        const dn = String(deptName).trim().toLowerCase();
        dept =
          departments.find((d) => d.name?.toLowerCase() === dn) ||
          departments.find(
            (d) => d.name && (d.name.toLowerCase().includes(dn) || dn.includes(d.name.toLowerCase()))
          );
      }
      // No matching department -> route to the user-selected fallback department
      // (never silently dropped).
      if (!dept) {
        dept = fallback;
        if (dept) reroutedCount += 1;
      }
      if (!dept) continue;

      const cur = updates[dept.id] || [...(dept.service_lines || [])];
      if (!cur.some((s) => s.toLowerCase() === clean.toLowerCase())) {
        cur.push(clean);
        created += 1;
        acceptedNames[dept.id] = [...(acceptedNames[dept.id] || []), clean];
      }
      updates[dept.id] = cur;
    }

    // Persist each affected department via existing PUT (partial-failure safe).
    const failed = [];
    const persisted = {};
    for (const [deptId, lines] of Object.entries(updates)) {
      try {
        await api.put(`/bcm-admin/departments/${deptId}`, { service_lines: lines });
        persisted[deptId] = lines;
      } catch (e) {
        failed.push(departments.find((d) => d.id === deptId)?.name || deptId);
      }
    }
    setDepartments((prev) => prev.map((d) => (persisted[d.id] ? { ...d, service_lines: persisted[d.id] } : d)));
    setSessionSources((prev) => {
      const next = { ...prev };
      for (const [deptId, names] of Object.entries(acceptedNames)) {
        if (!persisted[deptId]) continue;
        for (const n of names) next[`${deptId}::${n.toLowerCase()}`] = "ai";
      }
      return next;
    });
    setExpanded((prev) => {
      const next = { ...prev };
      for (const deptId of Object.keys(persisted)) next[deptId] = true;
      return next;
    });

    const createdOk = created - failed.reduce((n) => n, 0);
    if (reroutedCount > 0 && fallback) {
      toast.info(`${reroutedCount} suggestion(s) had no exact department match and were added to "${fallback.name}".`);
    }
    if (failed.length > 0) {
      return { created: createdOk, error: `Failed to save: ${failed.join(", ")}` };
    }
    return { created };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="service-lines-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="service-line-management-page">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center">
            <Layers className="w-6 h-6 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="service-lines-title">
              Service Line Management
            </h1>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Badge className="bg-blue-100 text-blue-700 border-blue-200" data-testid="service-lines-phase-badge">
                PLAN Phase · ISO 22301:4.1
              </Badge>
              {org?.name && (
                <span className="flex items-center gap-1" data-testid="service-lines-org-name">
                  <Building2 className="w-3.5 h-3.5" /> {org.name}
                </span>
              )}
            </div>
          </div>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Define the service lines each department delivers. These map operational capabilities to the
          business continuity scope.
        </p>
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="metric-total-departments">
          <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wide text-slate-400">Departments</p>
            <p className="text-3xl font-bold text-slate-900">{metrics.totalDepts}</p>
          </CardContent>
        </Card>
        <Card data-testid="metric-total-service-lines">
          <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wide text-slate-400">Total Service Lines</p>
            <p className="text-3xl font-bold text-blue-700">{metrics.totalLines}</p>
          </CardContent>
        </Card>
        <Card data-testid="metric-empty-departments">
          <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wide text-slate-400">Departments Without Lines</p>
            <p className="text-3xl font-bold text-amber-600">{metrics.emptyDepts}</p>
          </CardContent>
        </Card>
      </div>

      {/* AI Suggestions */}
      {departments.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-slate-600" data-testid="ai-fallback-dept-selector">
          <span className="text-slate-500">Add AI suggestions without an exact department match to:</span>
          <Select value={fallbackDeptId} onValueChange={setFallbackDeptId}>
            <SelectTrigger className="h-9 w-full sm:w-64" data-testid="ai-fallback-dept-trigger">
              <SelectValue placeholder="Select a department" />
            </SelectTrigger>
            <SelectContent>
              {departments.map((d) => (
                <SelectItem key={d.id} value={d.id} data-testid={`ai-fallback-dept-option-${d.id}`}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <AISuggestionsBanner
        module="service_lines"
        existingKeys={allServiceLineNames}
        itemKeyOf={(item) => (item && typeof item === "object" ? item.name : item)}
        onAccept={handleAcceptSuggestions}
        data-testid="service-lines-ai-banner"
      />

      {/* Department cards */}
      {departments.length === 0 ? (
        <Card data-testid="no-departments-state">
          <CardContent className="py-12 text-center text-slate-500">
            <Building2 className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            No departments found. Create departments in Organization Management first.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {departments.map((dept) => {
            const isOpen = !!expanded[dept.id];
            const lines = dept.service_lines || [];
            const critStyle = CRITICALITY_STYLES[dept.criticality] || CRITICALITY_STYLES.medium;
            return (
              <Card key={dept.id} data-testid={`department-card-${dept.id}`}>
                <CardHeader
                  className="cursor-pointer"
                  onClick={() => setExpanded((p) => ({ ...p, [dept.id]: !p[dept.id] }))}
                  data-testid={`department-toggle-${dept.id}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {isOpen ? (
                        <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <CardTitle className="text-base truncate">{dept.name}</CardTitle>
                        {dept.description && (
                          <p className="text-xs text-slate-500 truncate mt-0.5">{dept.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className={critStyle} data-testid={`department-criticality-${dept.id}`}>
                        {(dept.criticality || "medium").toUpperCase()}
                      </Badge>
                      <Badge variant="outline" className="bg-slate-50 text-slate-600" data-testid={`department-count-${dept.id}`}>
                        {lines.length} {lines.length === 1 ? "line" : "lines"}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>

                {isOpen && (
                  <CardContent className="space-y-4" data-testid={`department-body-${dept.id}`}>
                    {/* Recovery targets */}
                    {(isMeaningful(dept.suggested_rto) || isMeaningful(dept.suggested_mtpd)) && (
                      <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                        {isMeaningful(dept.suggested_rto) && (
                          <span data-testid={`department-rto-${dept.id}`}>
                            <span className="text-slate-400">Suggested RTO:</span>{" "}
                            <span className="font-medium">{dept.suggested_rto}</span>
                          </span>
                        )}
                        {isMeaningful(dept.suggested_mtpd) && (
                          <span data-testid={`department-mtpd-${dept.id}`}>
                            <span className="text-slate-400">Suggested MTPD:</span>{" "}
                            <span className="font-medium">{dept.suggested_mtpd}</span>
                          </span>
                        )}
                      </div>
                    )}

                    {/* Service line rows */}
                    {lines.length === 0 ? (
                      <div
                        className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 rounded-md p-3"
                        data-testid={`department-empty-${dept.id}`}
                      >
                        <AlertCircle className="w-4 h-4 text-amber-500" />
                        No service lines defined. Use AI Suggestions to auto-populate, or add manually.
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100 border border-slate-200 rounded-md">
                        {lines.map((line, index) => {
                          const isEditing =
                            editing && editing.deptId === dept.id && editing.index === index;
                          const src = sourceOf(dept.id, line);
                          return (
                            <div
                              key={`${dept.id}-${index}`}
                              className="flex items-center gap-2 p-2.5"
                              data-testid={`service-line-row-${dept.id}-${index}`}
                            >
                              {isEditing ? (
                                <>
                                  <Input
                                    value={editing.value}
                                    autoFocus
                                    onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                                    onKeyDown={(e) => e.key === "Enter" && handleSaveEdit()}
                                    className="h-8"
                                    data-testid={`service-line-edit-input-${dept.id}-${index}`}
                                  />
                                  <Button
                                    size="sm"
                                    onClick={handleSaveEdit}
                                    disabled={savingDept === dept.id}
                                    data-testid={`service-line-edit-save-${dept.id}-${index}`}
                                  >
                                    <Save className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditing(null)}
                                    data-testid={`service-line-edit-cancel-${dept.id}-${index}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Layers className="w-4 h-4 text-blue-500 shrink-0" />
                                  <span className="flex-1 text-sm text-slate-800">{line}</span>
                                  {src === "manual" && (
                                    <Badge className="bg-slate-100 text-slate-600 text-[10px]" data-testid={`service-line-source-manual-${dept.id}-${index}`}>
                                      Manual
                                    </Badge>
                                  )}
                                  {src === "ai" && (
                                    <Badge className="bg-sky-100 text-sky-700 text-[10px]" data-testid={`service-line-source-ai-${dept.id}-${index}`}>
                                      <Sparkles className="w-3 h-3 mr-0.5" /> AI-Generated
                                    </Badge>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditing({ deptId: dept.id, index, value: line })}
                                    data-testid={`service-line-edit-${dept.id}-${index}`}
                                  >
                                    <Pencil className="w-4 h-4 text-slate-400" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setDeleteTarget({ deptId: dept.id, index, name: line })}
                                    data-testid={`service-line-delete-${dept.id}-${index}`}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-400" />
                                  </Button>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Add row */}
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Add a service line (e.g. OCC, Dispatch)…"
                        value={addInputs[dept.id] || ""}
                        onChange={(e) => setAddInputs((p) => ({ ...p, [dept.id]: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && handleAdd(dept)}
                        className="h-9"
                        data-testid={`service-line-add-input-${dept.id}`}
                      />
                      <Button
                        onClick={() => handleAdd(dept)}
                        disabled={savingDept === dept.id || !(addInputs[dept.id] || "").trim()}
                        data-testid={`service-line-add-btn-${dept.id}`}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>

                    {/* Details */}
                    {(dept.primary_owner_name || dept.team_lead_name || (dept.location_ids?.length > 0)) && (
                      <div className="text-xs text-slate-500 bg-slate-50 rounded-md p-3 space-y-1" data-testid={`department-details-${dept.id}`}>
                        <p className="flex items-center gap-1 font-medium text-slate-600">
                          <Info className="w-3.5 h-3.5" /> Details
                        </p>
                        {dept.primary_owner_name && (
                          <p><span className="text-slate-400">Owner:</span> {dept.primary_owner_name}</p>
                        )}
                        {dept.team_lead_name && (
                          <p><span className="text-slate-400">Team Lead:</span> {dept.team_lead_name}</p>
                        )}
                        {dept.location_ids?.length > 0 && (
                          <p><span className="text-slate-400">Locations:</span> {dept.location_ids.length}</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-service-line-dialog">
          <DialogHeader>
            <DialogTitle>Delete Service Line</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <span className="font-medium">"{deleteTarget?.name}"</span>?
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="delete-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={savingDept === deleteTarget?.deptId}
              data-testid="delete-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
